import { createApiRef } from '@backstage/frontend-plugin-api';

export type RebuildMode = 'seed' | 'reset';
export type ContextCacheJobKind = 'full' | 'targeted';
export type ContextCacheJobStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'superseded';
export type ArtifactOutcome = 'succeeded' | 'degraded';

export interface ContextCacheJob {
  id: string;
  kind: ContextCacheJobKind;
  mode?: RebuildMode;
  targets: string[];
  status: ContextCacheJobStatus;
  startedBy: string;
  deliveryId?: string;
  attemptCount: number;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  heartbeatAt?: string;
  completedAt?: string;
  error?: string;
  cacheRevision?: string;
  artifactOutcomes?: Record<string, ArtifactOutcome>;
}

export interface JobLogsResponse {
  lines: string[];
  truncated: boolean;
}

export interface ContextRouterManifestStatus {
  scope: 'shared';
  manifestPath: string;
  exists: boolean;
  mtimeSeconds?: number;
  ageSeconds?: number;
  repoCount?: number;
  timestamp?: string;
  error?: string;
}

export interface ContextStatusResponse {
  status: 'ok' | 'degraded' | 'unavailable';
  jobs: {
    scope: 'cluster-wide';
    queuedCount: number;
    runningCount: number;
    queuedTargets: string[];
    runningTargets: string[];
    active: ContextCacheJob[];
    latestFull?: ContextCacheJob;
    latestTargeted?: ContextCacheJob;
  };
  publishedCache: {
    scope: 'shared';
    exists: boolean;
    readable: boolean;
    revision?: number;
    jobId?: string;
    completedAt?: string;
    artifacts?: Record<string, ArtifactOutcome>;
    degradedArtifacts?: string[];
    error?: string;
  };
  manifest: ContextRouterManifestStatus;
}

export interface ContextRouterApi {
  getRebuildStatus(): Promise<ContextCacheJob | null>;
  getJobStatus(jobId: string): Promise<ContextCacheJob>;
  getJobLogs(jobId: string): Promise<JobLogsResponse>;
  triggerRebuild(): Promise<ContextCacheJob>;
  getStatus(): Promise<ContextStatusResponse>;
}

export const contextRouterApiRef = createApiRef<ContextRouterApi>().with({
  id: 'plugin.context-router.api',
});
